import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";

import { Button, EmptyState, ErrorState, Notice, PageHeader, SkeletonRows } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import { useBusinessAuth } from "../../../context/BusinessAuthContext";
import ConfirmReceiptDialog from "./ConfirmReceiptDialog";
import PayslipDocument from "./PayslipDocument";
import MyCorrections from "../adjustments/MyCorrections";
import { MY_PAYSLIPS_BASE, confirmedMessage, employeeName, payDate, paymentOf, periodDates, periodLabel } from "./payslipView";

// One payslip at its own address (UI-3B): it can be linked, refreshed, gone back from and printed. The server
// returns it only to the employee it belongs to, and only once released; anyone else's looks like a missing one.

const PAGE_RULE = "@page { size: auto; margin: 15mm; }";

/** Paper size and margins for as long as the payslip page is open; later in the cascade than the parcel-label page. */
function usePayslipPaper() {
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-payslip-paper", "");
    style.textContent = PAGE_RULE;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);
}

export default function MyPayslip() {
  const { payslipId } = useParams();
  const auth = useBusinessAuth();
  const [state, setState] = useState({ status: "loading", payslip: null });
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState("");
  usePayslipPaper();

  const load = useCallback(async () => {
    try {
      const result = await businessApi.getMyPayslipDetails(payslipId);
      setState(result?.payslip ? { status: "ready", payslip: result.payslip } : { status: "missing", payslip: null });
    } catch (error) {
      const code = error?.response?.status ?? error?.status;
      setState({ status: code === 404 ? "missing" : "error", payslip: null });
    }
  }, [payslipId]);

  useEffect(() => {
    setState({ status: "loading", payslip: null });
    setNotice("");
    load();
  }, [load]);

  const back = (
    <Button as={Link} to={MY_PAYSLIPS_BASE} variant="quiet" size="sm" icon={ArrowLeft} className="-ml-3 mb-2">All payslips</Button>
  );
  const { status, payslip } = state;

  if (status !== "ready") {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div>
          {back}
          <PageHeader title="Payslip" />
        </div>
        <div className="rounded-panel border border-default bg-surface">
          {status === "loading" ? (
            <SkeletonRows rows={6} columns={2} label="Loading your payslip" />
          ) : status === "missing" ? (
            <EmptyState
              title="This payslip isn't available"
              description="It may not have been released yet. Your released payslips are listed in My Payslips."
              action={<Button as={Link} to={MY_PAYSLIPS_BASE}>Open My Payslips</Button>}
            />
          ) : (
            <ErrorState title="Your payslip couldn't be loaded" description="Check your connection and try again. Nothing has changed." onRetry={load} />
          )}
        </div>
      </div>
    );
  }

  const payment = paymentOf(payslip);
  const dates = periodDates(payslip);
  const meta = [dates !== periodLabel(payslip) ? dates : null, payDate(payslip) ? `Pay date ${payDate(payslip)}` : null].filter(Boolean).join(" · ");

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div>
        {back}
        <PageHeader
          title={periodLabel(payslip)}
          meta={meta || undefined}
          actions={<Button icon={Printer} onClick={() => window.print()}>Print payslip</Button>}
        />
      </div>

      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}

      {payment.key === "confirm" ? (
        <Notice tone="warning" title="Confirm that you received this salary">
          <p className="max-w-[75ch]">{payment.detail}</p>
          <Button variant="primary" className="mt-3 max-sm:w-full" onClick={() => setConfirming(true)}>Confirm salary received</Button>
        </Notice>
      ) : null}

      <PayslipDocument payslip={payslip} businessName={auth.businessName} employee={employeeName(auth.user)} />

      <MyCorrections payslipId={payslip.id} />

      {confirming ? (
        <ConfirmReceiptDialog
          payslip={payslip}
          onClose={() => { setConfirming(false); load(); }}
          onConfirmed={(outcome) => { setConfirming(false); setNotice(confirmedMessage(outcome)); load(); }}
        />
      ) : null}
    </div>
  );
}
